import React, { useState, useEffect } from 'react';
import { useThemeMode } from '../../ThemeContext';
import { usePermissions } from '../../contexts/PermissionContext';
import { dataAPI } from '../../helpers/api_service';
import { useNavigate } from 'react-router-dom';
import emptyDocIcon from '../../assets/empty_doc.svg';
import AddIcon from '@mui/icons-material/Add';
import RoleList from './RoleList';
import RoleForm from './RoleForm';

const Roles = () => {
  const { mode } = useThemeMode();
  const { hasAddPermission } = usePermissions();
  const navigate = useNavigate();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [roles, setRoles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch roles when component mounts
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        setIsLoading(true);
        setError('');
        const response = await dataAPI.getAllRoles();
        console.log('API Response:', response);
        console.log('Roles data:', response.data);
        setRoles(response.data || []);
      } catch (error) {
        console.error('Error fetching roles:', error);
        setError(error.message || 'Failed to fetch roles');
        setRoles([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRoles();
  }, []);

  // Debug: Log roles state changes
  useEffect(() => {
    console.log('Roles state updated:', roles);
    console.log('Roles length:', roles.length);
  }, [roles]);

  const handleDeleteRole = async (id) => {
    try {
      const response = await dataAPI.deleteRole(id);
      // Some APIs return 200 with status: 'error'
      if (response && (response.status === 'error' || response.success === false)) {
        const message = response.message || 'Failed to delete role';
        throw new Error(message);
      }
      setRoles(prev => prev.filter(role => role.id !== id));
    } catch (error) {
      console.error('Error deleting role:', error);
      setError(error.message || 'Failed to delete role');
      // Optionally, auto-clear the error after a short delay
      setTimeout(() => setError(''), 4000);
      // Re-throw so child awaiting can catch if needed
      throw error;
    }
  };

  const handleEditRole = async (role) => {
    try {
      console.log('Fetching modules with permissions for role:', role.id);
      
      // Call the API to get modules with permissions for this role
      const response = await dataAPI.getModulesWithPermissions(role.id);
      console.log('Modules with permissions response:', response);
      
      // Extract modules from the API response structure
      const modulesData = response.data?.modules || [];
      const roleName = response.data?.role_name || role.name;
      
      // Navigate to RolePermission component with the data
      navigate('/manage-employee/role-permission', {
        state: {
          modulesData: modulesData,
          roleName: roleName,
          roleDescription: role.description || '',
          roleId: role.id,
          isEditMode: true
        }
      });
    } catch (error) {
      console.error('Error fetching modules with permissions:', error);
      // Fallback to regular edit form if API fails
      setEditData(role);
      setIsFormOpen(true);
    }
  };

  return (
    <div style={{
      padding: '20px',
      width: '100%',
      height: '100%',
      backgroundColor: mode === 'dark' ? '#0f0f0f' : '#ffffff'
    }}>
      {/* Loading State */}
      {isLoading && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 'calc(100vh - 200px)'
        }}>
          <div style={{
            fontSize: '16px',
            color: mode === 'dark' ? '#FFFFFF' : '#666666',
            fontFamily: 'Urbanist, Arial, sans-serif'
          }}>
            Loading roles...
          </div>
        </div>
      )}

      {/* Error State */}
      {!isLoading && error && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: 'calc(100vh - 200px)',
          textAlign: 'center'
        }}>
          <div style={{
            fontSize: '16px',
            color: '#f44336',
            marginBottom: '16px',
            fontFamily: 'Urbanist, Arial, sans-serif'
          }}>
            {error}
          </div>
          <button 
            onClick={() => window.location.reload()}
            style={{
              background: '#26C3C0',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '8px',
              padding: '12px 24px',
              fontSize: '16px',
              fontWeight: '500',
              cursor: 'pointer',
              fontFamily: 'Urbanist, Arial, sans-serif'
            }}
          >
            Try Again
          </button>
        </div>
      )}

      {/* Content State */}
      {!isLoading && !error && roles.length === 0 ? (
        // Empty State
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: 'calc(100vh - 200px)',
          textAlign: 'center'
        }}>
          {/* Empty Role Icon */}
          <div style={{
            marginBottom: '24px'
          }}>
            <img 
              src={emptyDocIcon} 
              alt="No Roles" 
              style={{
                width: '71px',
                height: '59px',
                filter: mode === 'dark' ? 'brightness(1)' : 'brightness(0.8)'
              }}
            />
          </div>

          {/* Title */}
          <h1 style={{
            fontSize: '20px',
            fontWeight: '700',
            color: mode === 'dark' ? '#FFFFFF' : '#222222',
            margin: '0 0 16px 0',
            fontFamily: 'Urbanist, Arial, sans-serif',
            lineHeight: '1.2'
          }}>
            No Roles Yet
          </h1>

          {/* Description */}
          <p style={{
            fontSize: '16px',
            color: mode === 'dark' ? '#FFFFFF' : '#333333',
            margin: '0 0 32px 0',
            maxWidth: '600px',
            lineHeight: '1.5',
            fontFamily: 'Urbanist, Arial, sans-serif'
          }}>
            Create and assign roles to manage access across your organization. Define permissions that align with responsibilities to ensure secure and efficient collaboration.
          </p>

          {/* Add Role Button */}
          {hasAddPermission('employee') && (
            <button 
              onClick={() => setIsFormOpen(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                background: '#26C3C0',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '8px',
                padding: '12px 24px',
                fontSize: '16px',
                fontWeight: '500',
                cursor: 'pointer',
                fontFamily: 'Urbanist, Arial, sans-serif',
                transition: 'background-color 0.2s ease',
                boxShadow: '0 2px 8px rgba(38, 195, 192, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#22B0AD';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = '#26C3C0';
              }}
            >
              <AddIcon style={{ fontSize: '20px' }} />
              Add Role
            </button>
          )}
        </div>
      ) : (!isLoading && !error && roles.length > 0) ? (
        // Role List View
        <div>
          {/* Search and Add Buttons */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px',
            gap: '16px'
          }}>
            {/* Search Input */}
            <div style={{
              flex: 1,
              maxWidth: '400px'
            }}>
              <RoleList 
                roles={roles} 
                onDeleteRole={handleDeleteRole}
                onEditRole={handleEditRole}
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                showSearchOnly={true}
              />
            </div>

            {/* Add Role Button */}
            {hasAddPermission('employee') && (
              <button 
                onClick={() => setIsFormOpen(true)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  background: '#26C3C0',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px 24px',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  fontFamily: 'Urbanist, Arial, sans-serif',
                  transition: 'background-color 0.2s ease',
                  boxShadow: '0 2px 8px rgba(38, 195, 192, 0.3)',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#22B0AD';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = '#26C3C0';
                }}
              >
                <AddIcon style={{ fontSize: '20px' }} />
                Add Role
              </button>
            )}
          </div>

          {/* Role List Table */}
          <div>
            <RoleList 
              roles={roles} 
              onDeleteRole={handleDeleteRole}
              onEditRole={handleEditRole}
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              showSearchOnly={false}
            />
          </div>
        </div>
      ) : null}

      {/* Role Form - TODO: Create RoleForm component */}
      <RoleForm
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setEditData(null);
        }}
        editData={editData}
        onSubmit={(response) => {
          console.log('Role operation successful:', response);
          // Refresh the roles list
          const fetchRoles = async () => {
            try {
              const response = await dataAPI.getAllRoles();
              setRoles(response.data || []);
            } catch (error) {
              console.error('Error refreshing roles:', error);
            }
          };
          fetchRoles();
        }}
      />
    </div>
  );
};

export default Roles;
